import { useEffect } from 'react';
import useCustomer from '~/features/commerce-api/hooks/useCustomer';
import { useWishlist } from '~/features/commerce-api/hooks/wishlist/useWishList';
import { _transformAlgoliaToProductDetails } from '~/features/productList/Algoliga.utils';
import { useProductHandler } from '~/services/search/useProductHandler';
import { ProductCard } from '~/shared/components';
import { CardCarousel } from '~/shared/components/CardCarousel/CardCarousel';
import { TProductDetails } from '~/shared/components/ProductCard/ProductCard.definition';
import { useAlgoliaQuery } from '~/shared/hooks/useAlgoliaQuery';
import { useEvents } from '~/shared/hooks/useEvents/useEvents';
import { useUtils } from '~/shared/hooks/useUtils';
import { useFrame } from '~/shared/utils';
import { useTranslation } from '~/shared/utils/translation';
import { ModuleContainer } from '~/templates/blocks/components/ModuleContainer';
import { usePage } from '~/templates/pages';
import { theme } from '~/theme';
import { useProduct } from '../hooks/usePDPStore';
import { SFProduct } from '../pdp.definitions';
import { _transformProductDetailsToDataLayer } from '~/shared/hooks/useEvents/helpers';
import { GTM_LIST_ID, GTM_LIST_NAME } from '~/shared/hooks/useEvents/useGTMEvents/models';
import { useInView } from 'react-intersection-observer';

export const ProductsCarousels = () => {
    const { product } = useProduct();
    const { data: frame } = useFrame();
    const { productCategoryLinks } = usePage();
    const { productsImpressionsEvent } = useEvents(frame);
    const { translate } = useTranslation();
    const productSerie = product?.c_ProductSeries;
    const { convertPriceToLocale } = useUtils();
    const productHandler = useProductHandler();
    const { handleFavourite, isFavorite } = useWishlist();
    const productsInSerieLink = productCategoryLinks?.series ?? {};
    const { isPlusMember } = useCustomer();
    const { inView, ref } = useInView({
        threshold: 0,
        rootMargin: '0px 0px -500px 0px',
    });
    const { data: productsSerie } = useAlgoliaQuery<{ hits: SFProduct[] }, TProductDetails[]>(
        {
            filters: `cust_series: '${productSerie}' AND brand: '${product?.brand}'`,
        },
        {
            queryKey: [
                JSON.stringify({
                    filters: `cust_series: '${productSerie}' AND brand: '${product?.brand}'`,
                }),
            ],
            select: (data) => {
                return data.hits.map((hit) =>
                    _transformAlgoliaToProductDetails(hit, translate, convertPriceToLocale),
                ) as TProductDetails[];
            },
            enabled: !!productSerie,
        },
    );

    useEffect(() => {
        if (!inView || !productsSerie?.length) return;

        const filteredProducts = productsSerie.filter((x) => x.id !== product?.id);

        if (filteredProducts.length > 0) {
            productsImpressionsEvent(
                GTM_LIST_ID.PRODUCT_LIST,
                `${GTM_LIST_NAME.CAROUSEL} - ${translate('content.partOfSeries')}`,
                filteredProducts.map((item, index) =>
                    _transformProductDetailsToDataLayer(item, 1, index, isPlusMember),
                ),
            );
        }
    }, [inView, productsSerie]);

    const showProductsSerie =
        (productsSerie && productsSerie.filter((x) => x.id !== product?.id).length > 0) ?? false;

    return (
        <div>
            {showProductsSerie && (
                <ModuleContainer backgroundColor={theme.colors.brownLight} ref={ref}>
                    <CardCarousel
                        callToAction={{ ...productsInSerieLink, text: translate('plp.seeAll') }}
                        options={{
                            slidesPerView: 'auto',
                            spaceBetween: 24,
                        }}
                        headline={{
                            tag: 'h2',
                            text: translate('pdp.productsInSerie'),
                            style: 'display2',
                        }}
                        items={productsSerie
                            ?.filter((x) => x.id !== product?.id)
                            .map((p, i) => (
                                <ProductCard
                                    {...p}
                                    position={i}
                                    listId={translate('content.partOfSeries')}
                                    listName={translate('content.partOfSeries')}
                                    onBuyBtn={productHandler}
                                    onFavourite={handleFavourite}
                                    isFavourite={isFavorite(p.id)}
                                />
                            ))}
                    />
                </ModuleContainer>
            )}
        </div>
    );
};
